import { useMemo } from 'react';

import { evaluateDownload } from './evaluate';
import { assetGroupIsLut } from './internal/assetGroupIsLut';
import { useGatewaySnapshot } from './internal/snapshot';
import { computeStockVideoBundle } from './internal/stockVideoTier';

import type { AssetGroup } from '@/types/asset';
import type { Permission } from '@/types/auth';
import type { AssetRef, DownloadDecision, RequestedFormat } from './types';

interface UseDownloadGatewayResult {
  decision: DownloadDecision;
  /**
   * Reserved for a future iteration. Today the modal owns the imperative
   * download wiring via `setDirectDownloadAllowed` / `setIsDialogOpen`;
   * this exists so callers can begin switching to a single `trigger()`
   * API now and stop reaching into modal state directly later.
   */
  trigger: () => void;
}

/**
 * Map the caller's natural `AssetGroup` shape to the gateway's compact
 * `AssetRef`. Audio + motion mappings are pure; stock-video also needs the
 * user's permissions to compute the bundle elevation (`pro` / `creator` /
 * null), which is cached on the AssetRef and consulted by
 * `evaluateStockVideo`.
 */
const toAssetRef = (
  asset: AssetGroup,
  permissions: ReadonlySet<Permission>
): AssetRef => {
  if (asset.type === 'music' || asset.type === 'sfx') {
    return {
      kind: asset.type,
      id: asset.data.id,
      isPremium: asset.data.is_premium,
    };
  }

  if (asset.type === 'motion') {
    return {
      kind: assetGroupIsLut({ asset }) ? 'lut' : 'mg',
      id: Number(asset.data.id),
      isPremium: asset.data.is_premium,
    };
  }

  // stock_footage
  return {
    kind: 'stock-video',
    id: Number(asset.data.id ?? 0),
    isPremium: !!asset.data.is_premium,
    bundle: computeStockVideoBundle(permissions),
  };
};

export const useDownloadGateway = (
  asset: AssetGroup,
  requested?: RequestedFormat
): UseDownloadGatewayResult => {
  const snapshot = useGatewaySnapshot();

  const assetRef = useMemo(
    () => toAssetRef(asset, snapshot.permissions),
    [asset, snapshot.permissions]
  );

  const decision = useMemo(
    () => evaluateDownload({ asset: assetRef, requested }, snapshot),
    [assetRef, requested, snapshot]
  );

  // No-op until the modal's download wiring moves behind this API; see the
  // doc on `UseDownloadGatewayResult.trigger`.
  const trigger = useMemo(() => () => undefined, []);

  return { decision, trigger };
};
